import { FormikProps, getIn } from 'formik';
import React, { useContext } from 'react';
import { formatCurrency } from '../../helpers/stringHelpers';
import { ReactComponent as DeleteIcon } from '../../images/icons/delete.svg';
import { ButtonIcon, SecondaryButton } from '../../shared/Button';
import { FormContext } from '../../shared/form/Form';
import { TextInputField } from '../../shared/form/inputs/TextInputField';
import { Tr } from '../../shared/Table';
import { EditDesignBillOfMaterialsFormModel } from '../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import { ViewOrEditQuoteBillOfMaterialsFormModel } from '../quotes/view-or-edit-bill-of-materials/viewOrEditQuoteBillOfMaterialsFormModel';
import { useBillOfMaterialsPricingModelInfo } from './BillOfMaterialsContext';
import {
  ActionButtonContainer,
  ActionButtonTd,
  CurrencyTd,
  DescriptionTd,
  InactivePartErrorIndicator,
  PartCodeContainer,
  PartCodeTd,
  QuantityTd,
} from './BillOfMaterialsItemsTable';
import { BillOfMaterialsOptionalExtraFormModel } from './billOfMaterialsOptionalExtraFormModel';
import { BillOfMaterialsPartsById, getPartPriceBasedOnCustomerType } from './dataForEditBillOfMaterialsForm';

type Props = {
  fieldName: string;
  optionalExtra: BillOfMaterialsOptionalExtraFormModel;
  discountPercentage?: number | null;
  partsById: BillOfMaterialsPartsById;
  deleteOptionalExtra: () => void;
  errors: FormikProps<EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel>['errors'];
};

export const BillOfMaterialsOptionalExtraField = ({
  optionalExtra,
  partsById,
  deleteOptionalExtra,
  fieldName,
  errors,
}: Props) => {
  const { isUsingLegacyPricingModel, selectedCustomerTypeOption } = useBillOfMaterialsPricingModelInfo();

  const part = optionalExtra.partId == null ? null : partsById[optionalExtra.partId] || null;
  const { isDisabled } = useContext(FormContext);

  if (part == null) {
    throw new Error(`Couldn't find Part with ID ${optionalExtra.partId}`);
  }

  const price =
    Number(getPartPriceBasedOnCustomerType(isUsingLegacyPricingModel, part, selectedCustomerTypeOption)) *
    Number(optionalExtra.quantity);

  const partIsInactiveValidationFieldName = `${fieldName}.partIsInactiveValidation`;
  const partIsInactiveValidationError = getIn(errors, partIsInactiveValidationFieldName);

  return (
    <Tr>
      <PartCodeTd>
        <PartCodeContainer $hasError={partIsInactiveValidationError != null}>
          {part.name}
          <InactivePartErrorIndicator
            fieldName={partIsInactiveValidationFieldName}
            showError={partIsInactiveValidationError != null}
            error={partIsInactiveValidationError}
          />
        </PartCodeContainer>
      </PartCodeTd>
      <QuantityTd>
        <TextInputField
          name={`${fieldName}.quantity`}
          label={null}
          testId={billOfMaterialsOptionalExtraQuantityFieldTestId(fieldName)}
        />
      </QuantityTd>
      <DescriptionTd>{part.description}</DescriptionTd>
      <CurrencyTd>{isNaN(price) ? null : formatCurrency(price)}</CurrencyTd>
      <ActionButtonTd>
        {!isDisabled && (
          <ActionButtonContainer>
            <SecondaryButton onClick={deleteOptionalExtra} disabled={false} title="Delete">
              <ButtonIcon>
                <DeleteIcon title="Delete" data-testid={deleteBillOfMaterialsOptionalExtraButtonTestId(fieldName)} />
              </ButtonIcon>
            </SecondaryButton>
          </ActionButtonContainer>
        )}
      </ActionButtonTd>
    </Tr>
  );
};

export const deleteBillOfMaterialsOptionalExtraButtonTestId = (fieldName: string) =>
  `delete-bom-optional-extra-${fieldName}`;

export const billOfMaterialsOptionalExtraQuantityFieldTestId = (fieldName: string) =>
  `bom-optional-extra-quantity-${fieldName}`;
